import { ButtonGroup, Grid, Tooltip, useMediaQuery } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { makeStyles } from "@mui/styles";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import Replay30Icon from "@mui/icons-material/Replay30";
import Replay10Icon from "@mui/icons-material/Replay10";
import Replay5Icon from "@mui/icons-material/Replay5";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import Forward5Icon from "@mui/icons-material/Forward5";
import Forward10Icon from "@mui/icons-material/Forward10";
import Forward30Icon from "@mui/icons-material/Forward30";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import CloseIcon from "@mui/icons-material/Close";

import { theme } from "../../styles/theme";

const useStyles = makeStyles({
  videoPlayer: {
    display: "flex",
    flexDirection: "column !important",
    alignItems: "center",
    justifyContent: "center",
    padding: "1rem 1rem 1rem 0",
    gap: "1rem",
    "& video": {
      width: "100%",
      maxWidth: "800px",
      height: "31vh",
      backgroundColor: "black",
      objectFit: "contain",
    },
    overflow: "hidden",
  },
  videoPlayerModal: {
    display: "flex",
    flexDirection: "column !important",
    alignItems: "center",
    justifyContent: "center",
    // padding: "1rem 1rem 1rem 0",
    gap: "1rem",
    "& video": {
      marginTop: "0px",
      width: "100%",
      maxWidth: "100%",
      height: "94vh",
      backgroundColor: "black",
      objectFit: "contain",
    },
    overflow: "hidden",
  },
  icons: {
    cursor: "pointer",
    color: "black",
  },
  disabledIcon: {
    cursor: "not-allowed",
    color: "#787878",
  },
  buttonGroup: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    gap: "10px !important",
    [theme.breakpoints.down(1460)]: {
      gap: "10px !important",
    },
    [theme.breakpoints.down(1380)]: {
      gap: "8px !important",
    },
    [theme.breakpoints.down(1330)]: {
      gap: "6px !important",
    },
    [theme.breakpoints.down(1260)]: {
      gap: "5px !important",
    },
    [theme.breakpoints.down(1220)]: {
      gap: "3px !important",
    },
    [theme.breakpoints.down(1180)]: {
      gap: "1px !important",
    },
    [theme.breakpoints.down(1100)]: {
      gap: "0px !important",
    },
  },
  buttonGroupMobile: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    gap: "25px !important",
    [theme.breakpoints.down(650)]: {
      gap: "15px !important",
    },
    [theme.breakpoints.down(520)]: {
      gap: "5px !important",
    },
    [theme.breakpoints.down(410)]: {
      gap: "0px !important",
    },
  },
  modal: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "100%",
    height: "100%",
    bgcolor: "background.paper",
    border: "2px solid #000",
    boxShadow: "24 !important",
    padding: "10px !important",
    backgroundColor: "white !important",
    borderRadius: "10px",
    border: "none",
  },
  closeButton: {
    position: "absolute",
    top: "20px",
    right: "20px",
    cursor: "pointer",
    color: "black",
    zIndex: "1000",
    color: "white",
  },
});

const VideoStream = ({ videoRef, videoSrc }) => {
  const classes = useStyles();

  const modalVideoRef = useRef(null);
  const playbackSpeedOptions = [0.25, 0.5, 1, 2, 5, 10]; // Custom playback speed options

  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPlayingModal, setIsPlayingModal] = useState(false);
  const [frameRate, setFrameRate] = useState(10);
  const matches = useMediaQuery("(min-width:900px)");
  const isBelow1100px = useMediaQuery(theme.breakpoints.down(1100));
  const [open, setOpen] = React.useState(false);

  useEffect(() => {
    const handlePlay = () => {
      setIsPlaying(true);
    };

    const handlePause = () => {
      setIsPlaying(false);
    };

    const currentVideoRef = videoRef.current; // Store the current ref value

    if (currentVideoRef) {
      currentVideoRef.addEventListener("play", handlePlay);
      currentVideoRef.addEventListener("pause", handlePause);
    }

    return () => {
      if (currentVideoRef) {
        currentVideoRef.removeEventListener("play", handlePlay);
        currentVideoRef.removeEventListener("pause", handlePause);
      }
    };
  }, []);

  useEffect(() => {
    const handlePlay = () => {
      setIsPlayingModal(true);
    };

    const handlePause = () => {
      setIsPlayingModal(false);
    };

    const currentModalVideoRef = modalVideoRef.current; // Store the current ref value

    if (currentModalVideoRef) {
      currentModalVideoRef.addEventListener("play", handlePlay);
      currentModalVideoRef.addEventListener("pause", handlePause);
    }

    return () => {
      if (currentModalVideoRef) {
        currentModalVideoRef.removeEventListener("play", handlePlay);
        currentModalVideoRef.removeEventListener("pause", handlePause);
      }
    };
  }, [open]);

  const handlePlaybackSpeedChange = (speed) => {
    if (open) {
      modalVideoRef.current.playbackRate = speed;
    } else {
      videoRef.current.playbackRate = speed;
    }
    setPlaybackSpeed(speed);
  };

  const handleNextPlaybackSpeed = () => {
    const currentIndex = playbackSpeedOptions.indexOf(playbackSpeed);
    const nextIndex = currentIndex + 1;

    if (nextIndex < playbackSpeedOptions.length) {
      const nextSpeed = playbackSpeedOptions[nextIndex];
      handlePlaybackSpeedChange(nextSpeed);
    }
  };

  const handlePreviousPlaybackSpeed = () => {
    const currentIndex = playbackSpeedOptions.indexOf(playbackSpeed);
    const previousIndex = currentIndex - 1;

    if (previousIndex >= 0) {
      const previousSpeed = playbackSpeedOptions[previousIndex];
      handlePlaybackSpeedChange(previousSpeed);
    }
  };

  const handlePlayPause = () => {
    if (open) {
      if (modalVideoRef.current.paused) {
        modalVideoRef.current.play();
        setIsPlayingModal(true);
      } else {
        modalVideoRef.current.pause();
        setIsPlayingModal(false);
      }
    } else {
      if (videoRef.current.paused) {
        videoRef.current.play();
      } else {
        videoRef.current.pause();
      }
    }
  };

  const handleFrameNavigation = (frames) => {
    if (open) {
      modalVideoRef.current.pause();
      const frameDuration = 1 / frameRate;
      modalVideoRef.current.currentTime += frames * frameDuration;
    } else {
      videoRef.current.pause();
      const frameDuration = 1 / frameRate;
      videoRef.current.currentTime += frames * frameDuration;
    }
  };

  const handleOpen = () => {
    setOpen(true); // Open the modal first

    // Ensure the modal video is synced with the background video
    setTimeout(() => {
      if (videoRef.current && modalVideoRef.current) {
        // Sync time and playback rate
        modalVideoRef.current.currentTime = videoRef.current.currentTime;
        modalVideoRef.current.playbackRate = videoRef.current.playbackRate;

        if (videoRef.current.paused) {
          modalVideoRef.current.pause(); // Pause the modal video if the background video is paused
        } else {
          modalVideoRef.current.play(); // Play the modal video if the background video is playing
        }

        // Pause the background video to avoid both playing simultaneously
        videoRef.current.pause();
      }
    }, 0); // Use a small timeout to ensure the modal is open before syncing
  };

  const handleClose = () => {
    if (modalVideoRef.current && videoRef.current) {
      // Sync the current video state with the modal video state when exiting fullscreen
      videoRef.current.currentTime = modalVideoRef.current.currentTime;
      videoRef.current.playbackRate = modalVideoRef.current.playbackRate;

      if (!modalVideoRef.current.paused) {
        videoRef.current.play(); // Play the original video if modal was playing
      } else {
        videoRef.current.pause(); // Pause the original video if modal was paused
      }
    }
    setOpen(false);
  };

  return (
    <>
      <Grid container className={classes.videoPlayer}>
        <video ref={videoRef} controls src={videoSrc} />
        <Grid
          justifyContent="space-between"
          display="flex"
          width="100%"
          maxWidth={"800px"}
          gap={isBelow1100px && matches ? 0 : 2}
        >
          <Grid justifyContent="flex-start" alignItems="center" display="flex" width="100%" maxWidth={"800px"} gap={2}>
            <Grid>
              <ButtonGroup className={matches ? classes.buttonGroup : classes.buttonGroupMobile}>
                <Tooltip title="Move to previous frame">
                  <ArrowBackIosNewIcon className={classes.icons} onClick={() => handleFrameNavigation(-1)} />
                </Tooltip>
                {isPlaying ? (
                  <Tooltip title="Pause video">
                    <PauseIcon className={classes.icons} onClick={handlePlayPause} />
                  </Tooltip>
                ) : (
                  <Tooltip title="Play video">
                    <PlayArrowIcon className={classes.icons} onClick={handlePlayPause} />
                  </Tooltip>
                )}
                <Tooltip title="Move to next frame">
                  <ArrowForwardIosIcon className={classes.icons} onClick={() => handleFrameNavigation(1)} />
                </Tooltip>
              </ButtonGroup>
            </Grid>
            <Grid>
              <ButtonGroup className={matches ? classes.buttonGroup : classes.buttonGroupMobile}>
                <Tooltip title="Replay last 30 sec" sx={{ display: isBelow1100px && matches ? "none" : "" }}>
                  <Replay30Icon className={classes.icons} onClick={() => (videoRef.current.currentTime -= 30)} />
                </Tooltip>
                <Tooltip title="Replay last 10 sec">
                  <Replay10Icon className={classes.icons} onClick={() => (videoRef.current.currentTime -= 10)} />
                </Tooltip>
                <Tooltip title="Replay last 5 sec">
                  <Replay5Icon className={classes.icons} onClick={() => (videoRef.current.currentTime -= 5)} />
                </Tooltip>
                <Tooltip title="Decrease Playback Speed">
                  <KeyboardDoubleArrowLeftIcon
                    className={playbackSpeed === playbackSpeedOptions[0] ? classes.disabledIcon : classes.icons}
                    onClick={handlePreviousPlaybackSpeed}
                  />
                </Tooltip>
                <Grid sx={{ fontWeight: 600, fontSize: 18, color: "#787878" }}>{playbackSpeed}x</Grid>
                <Tooltip title="Increase Playback Speed">
                  <KeyboardDoubleArrowRightIcon
                    className={
                      playbackSpeed === playbackSpeedOptions[playbackSpeedOptions.length - 1]
                        ? classes.disabledIcon
                        : classes.icons
                    }
                    onClick={handleNextPlaybackSpeed}
                  />
                </Tooltip>
                <Tooltip title="Forward by 5 sec">
                  <Forward5Icon className={classes.icons} onClick={() => (videoRef.current.currentTime += 5)} />
                </Tooltip>
                <Tooltip title="Forward by 10 sec">
                  <Forward10Icon className={classes.icons} onClick={() => (videoRef.current.currentTime += 10)} />
                </Tooltip>
                <Tooltip title="Forward by 30 sec" sx={{ display: isBelow1100px && matches ? "none" : "" }}>
                  <Forward30Icon className={classes.icons} onClick={() => (videoRef.current.currentTime += 30)} />
                </Tooltip>
              </ButtonGroup>
            </Grid>
          </Grid>
          <Grid alignItems="center" justifyContent="flex-end">
            <Tooltip title="Full Screen">
              <FullscreenIcon className={classes.icons} style={{ fontSize: "29px" }} onClick={handleOpen} />
            </Tooltip>
          </Grid>
        </Grid>
      </Grid>
      <Modal
        aria-labelledby="modal-title"
        aria-describedby="modal-desc"
        open={open}
        onClose={handleClose}
        sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
      >
        <Box className={classes.modal}>
          <CloseIcon className={classes.closeButton} fontSize="large" onClick={handleClose} />
          <Grid container className={classes.videoPlayerModal} justifyContent="center">
            <video ref={modalVideoRef} controls src={videoSrc} />
            <Grid justifyContent="space-between" display="flex" width="100%" maxWidth={"800px"} gap={2}>
              <Grid
                justifyContent="flex-start"
                alignItems="center"
                display="flex"
                width="100%"
                maxWidth={"800px"}
                gap={3}
              >
                <Grid>
                  <ButtonGroup className={matches ? classes.buttonGroup : classes.buttonGroupMobile}>
                    <Tooltip title="Move to previous frame">
                      <ArrowBackIosNewIcon className={classes.icons} onClick={() => handleFrameNavigation(-1)} />
                    </Tooltip>
                    {isPlayingModal ? (
                      <Tooltip title="Pause video">
                        <PauseIcon className={classes.icons} onClick={handlePlayPause} />
                      </Tooltip>
                    ) : (
                      <Tooltip title="Play video">
                        <PlayArrowIcon className={classes.icons} onClick={handlePlayPause} />
                      </Tooltip>
                    )}
                    <Tooltip title="Move to next frame">
                      <ArrowForwardIosIcon className={classes.icons} onClick={() => handleFrameNavigation(1)} />
                    </Tooltip>
                  </ButtonGroup>
                </Grid>
                <Grid>
                  <ButtonGroup className={matches ? classes.buttonGroup : classes.buttonGroupMobile}>
                    <Tooltip title="Replay last 30 sec">
                      <Replay30Icon
                        className={classes.icons}
                        onClick={() => (modalVideoRef.current.currentTime -= 30)}
                      />
                    </Tooltip>
                    <Tooltip title="Replay last 10 sec">
                      <Replay10Icon
                        className={classes.icons}
                        onClick={() => (modalVideoRef.current.currentTime -= 10)}
                      />
                    </Tooltip>
                    <Tooltip title="Replay last 5 sec">
                      <Replay5Icon className={classes.icons} onClick={() => (modalVideoRef.current.currentTime -= 5)} />
                    </Tooltip>
                    <Tooltip title="Decrease Playback Speed">
                      <KeyboardDoubleArrowLeftIcon
                        className={playbackSpeed === playbackSpeedOptions[0] ? classes.disabledIcon : classes.icons}
                        onClick={handlePreviousPlaybackSpeed}
                      />
                    </Tooltip>
                    <Grid sx={{ fontWeight: 600, fontSize: 18, color: "#787878" }}>{playbackSpeed}x</Grid>
                    <Tooltip title="Increase Playback Speed">
                      <KeyboardDoubleArrowRightIcon
                        className={
                          playbackSpeed === playbackSpeedOptions[playbackSpeedOptions.length - 1]
                            ? classes.disabledIcon
                            : classes.icons
                        }
                        onClick={handleNextPlaybackSpeed}
                      />
                    </Tooltip>
                    <Tooltip title="Forward by 5 sec">
                      <Forward5Icon
                        className={classes.icons}
                        onClick={() => (modalVideoRef.current.currentTime += 5)}
                      />
                    </Tooltip>
                    <Tooltip title="Forward by 10 sec">
                      <Forward10Icon
                        className={classes.icons}
                        onClick={() => (modalVideoRef.current.currentTime += 10)}
                      />
                    </Tooltip>
                    <Tooltip title="Forward by 30 sec">
                      <Forward30Icon
                        className={classes.icons}
                        onClick={() => (modalVideoRef.current.currentTime += 30)}
                      />
                    </Tooltip>
                  </ButtonGroup>
                </Grid>
              </Grid>
              <Grid alignItems="center" justifyContent="flex-end">
                <Tooltip title="Exit Full Screen">
                  <FullscreenExitIcon className={classes.icons} style={{ fontSize: "29px" }} onClick={handleClose} />
                </Tooltip>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Modal>
    </>
  );
};

export default VideoStream;
